import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError, fmtDate, REMINDER_CATEGORIES, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/pages/AppLayout";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, FileDown, Plus, Wrench, ClipboardList, Bell, Printer, X,
  ClipboardCheck, AlertTriangle, ShieldCheck, History,
} from "lucide-react";

export default function MachineDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isManager, isFree } = useAuth();
  const [data, setData] = useState(null);
  const [prestarts, setPrestarts] = useState([]);
  const [flagHistory, setFlagHistory] = useState([]);
  const [remForm, setRemForm] = useState({ category: "service", title: "", due_date: "", interval_days: "", interval_hours: "", interval_km: "" });
  const [remOpen, setRemOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/machines/${id}`).then(({ data }) => setData(data));
    api.get(`/machines/${id}/prestarts`).then(({ data }) => setPrestarts(data)).catch(() => {});
    api.get(`/machines/${id}/flag-history`).then(({ data }) => setFlagHistory(data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-6 md:p-10 font-head text-2xl text-[#FFB800] uppercase tracking-widest">Loading…</div>;
  const { machine: m, reminders, services } = data;

  const addReminder = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reminders", {
        machine_id: id, category: remForm.category, title: remForm.title || remForm.category,
        due_date: remForm.due_date ? new Date(remForm.due_date).toISOString() : null,
        interval_days: remForm.interval_days ? parseInt(remForm.interval_days) : null,
        interval_hours: remForm.interval_hours ? parseFloat(remForm.interval_hours) : null,
        interval_km: remForm.interval_km ? parseFloat(remForm.interval_km) : null,
      });
      toast.success("Reminder added");
      setRemOpen(false);
      setRemForm({ category: "service", title: "", due_date: "", interval_days: "", interval_hours: "", interval_km: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const delReminder = async (rid) => {
    await api.delete(`/reminders/${rid}`); toast.success("Reminder removed"); load();
  };

  const delMachine = async () => {
    try { await api.delete(`/machines/${id}`); toast.success("Machine deleted"); nav("/machines"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const setCover = async (photoId) => {
    try { await api.patch(`/machines/${id}/cover/${photoId}`); toast.success("Cover photo updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const unflag = async () => {
    try { await api.patch(`/machines/${id}/unflag`); toast.success("Machine cleared"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const pdfUrl = `${API}/machines/${id}/pdf?token=${localStorage.getItem("ironlog_token")}`;
  const qrUrl = `${window.location.origin}/m/${id}`;
  const spec = (label, val) => (
    <div className="border border-white/10 p-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{label}</p>
      <p className="font-mono text-lg mt-1 truncate">{val || "—"}</p>
    </div>
  );

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="machine-detail-page">
      <Link to="/machines" className="flex items-center gap-2 text-[#94A3B8] font-body text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Machines</Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <p className="font-mono text-xs uppercase tracking-widest text-[#FFB800]">{m.type}</p>
            <StatusBadge status={m.health} />
          </div>
          <h1 className="font-head font-900 text-5xl md:text-6xl uppercase tracking-tighter leading-none mt-1">{m.make} {m.model}</h1>
          <p className="font-body text-[#94A3B8] mt-2 text-sm">Added by {m.added_by_name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/machines/${id}/log`} className="btn-signal h-12 px-5 font-head flex items-center gap-2" data-testid="log-service-btn"><ClipboardList className="w-4 h-4" /> Log Service</Link>
          {!isFree && <button onClick={() => setQrOpen(true)} className="btn-ghost h-12 px-4 font-head flex items-center gap-2" data-testid="qr-btn"><Printer className="w-4 h-4" /> QR</button>}
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-ghost h-12 px-4 font-head flex items-center gap-2" data-testid="pdf-btn"><FileDown className="w-4 h-4" /> PDF</a>
          <Link to={`/machines/${id}/edit`} className="btn-ghost h-12 px-4 font-head flex items-center gap-2" data-testid="edit-machine-btn"><Pencil className="w-4 h-4" /></Link>
          {isManager && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="h-12 px-4 border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors flex items-center gap-2" data-testid="delete-machine-btn"><Trash2 className="w-4 h-4" /></button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#1A1C23] border border-white/10 text-white">
                <AlertDialogHeader><AlertDialogTitle className="font-head uppercase">Delete {m.make} {m.model}?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#94A3B8]">This removes the machine, its reminders and service history. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="btn-ghost border-white/20">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={delMachine} className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80" data-testid="confirm-delete-machine">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {m.flagged && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-4 mb-8" data-testid="machine-flagged-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-[#FF3B30] mt-0.5 shrink-0" />
            <div>
              <p className="font-head font-800 uppercase tracking-wide text-[#FF3B30]">Flagged — Fault Reported</p>
              <p className="font-body text-sm text-[#94A3B8] mt-1">{m.flag_reason || "Outstanding pre-start fault."}</p>
            </div>
          </div>
          {isManager && <button onClick={unflag} className="btn-signal h-10 px-4 font-head text-sm shrink-0 self-start sm:self-center" data-testid="unflag-btn">Mark Resolved</button>}
        </div>
      )}

      {/* Photo gallery */}
      {m.photo_count > 0 && (
        <div className="mb-8 max-w-2xl" data-testid="machine-gallery">
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2">
            {Array.from({ length: m.photo_count }).map((_, i) => (
              <div key={i} className="snap-center shrink-0 w-full relative">
                <img
                  src={`${API}/public/machines/${id}/photo/${i}`}
                  alt={`${m.make} ${m.model} photo ${i + 1}`}
                  className="w-full max-h-96 object-cover border border-white/10"
                  data-testid={`machine-photo-${i}`}
                />
                {i === 0 && <span className="absolute top-2 left-2 bg-[#FFB800] text-black font-mono text-[10px] uppercase tracking-widest px-2 py-1" data-testid="cover-badge">Cover</span>}
                {isManager && i !== 0 && m.photo_ids && m.photo_ids[i] && (
                  <button onClick={() => setCover(m.photo_ids[i])} className="absolute top-2 right-2 bg-[#0D0E11]/80 border border-white/20 px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-[#FFB800] hover:text-black transition-colors" data-testid={`set-cover-${i}`}>Set cover</button>
                )}
              </div>
            ))}
          </div>
          {m.photo_count > 1 && <p className="font-mono text-[11px] text-[#94A3B8] mt-1">{m.photo_count} photos · swipe to view</p>}
        </div>
      )}

      {/* Specs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {spec("Fleet No.", m.fleet_number)}
        {spec("Serial", m.serial)}
        {spec("Year", m.year)}
        {spec("Running Hours", `${m.running_hours || 0}h`)}
        {spec("Odometer", `${m.odometer_km || 0} km`)}
        {spec("Rego No.", m.rego_number)}
        {spec("Rego Renewal", fmtDate(m.rego_renewal_date))}
        {spec("Overdue", m.overdue_count)}
        {spec("Upcoming", m.upcoming_count)}
        {spec("Notes", m.notes)}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Reminders */}
        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2"><Bell className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Reminders</h2></div>
            <Dialog open={remOpen} onOpenChange={setRemOpen}>
              {!isFree && <DialogTrigger asChild><button className="btn-signal h-9 px-3 font-head text-sm flex items-center gap-1" data-testid="add-reminder-btn"><Plus className="w-4 h-4" /> Add</button></DialogTrigger>}
              <DialogContent className="bg-[#1A1C23] border border-white/10 text-white">
                <DialogHeader><DialogTitle className="font-head uppercase text-2xl">New Reminder</DialogTitle></DialogHeader>
                <form onSubmit={addReminder} className="space-y-4" data-testid="reminder-form">
                  <div><label className="il-label">Category</label>
                    <select className="il-input" value={remForm.category} onChange={(e) => setRemForm({ ...remForm, category: e.target.value })} data-testid="reminder-category">
                      {REMINDER_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
                    </select></div>
                  <div><label className="il-label">Title</label><input className="il-input" value={remForm.title} onChange={(e) => setRemForm({ ...remForm, title: e.target.value })} placeholder="e.g. 250h engine service" data-testid="reminder-title" /></div>
                  <div><label className="il-label">Due Date</label><input className="il-input font-mono" type="date" value={remForm.due_date} onChange={(e) => setRemForm({ ...remForm, due_date: e.target.value })} data-testid="reminder-due" /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="il-label">Repeat (days)</label><input className="il-input font-mono" type="number" value={remForm.interval_days} onChange={(e) => setRemForm({ ...remForm, interval_days: e.target.value })} placeholder="90" data-testid="reminder-days" /></div>
                    <div><label className="il-label">Repeat (hours)</label><input className="il-input font-mono" type="number" value={remForm.interval_hours} onChange={(e) => setRemForm({ ...remForm, interval_hours: e.target.value })} placeholder="250" data-testid="reminder-hours" /></div>
                    <div><label className="il-label">Repeat (km)</label><input className="il-input font-mono" type="number" value={remForm.interval_km} onChange={(e) => setRemForm({ ...remForm, interval_km: e.target.value })} placeholder="10000" data-testid="reminder-km" /></div>
                  </div>
                  <DialogFooter><button className="btn-signal w-full h-12 font-head" data-testid="reminder-submit">Add Reminder</button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {isFree ? <p className="p-5 font-body text-[#94A3B8] text-sm" data-testid="reminders-locked">Reminders are a paid feature. <Link to="/billing" className="text-[#FFB800]">Upgrade →</Link></p> : reminders.length === 0 ? <p className="p-5 font-body text-[#94A3B8] text-sm">No reminders yet.</p> : (
            <div className="divide-y divide-white/10">
              {reminders.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-5 py-3.5" data-testid={`reminder-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-700 truncate">{r.title}</p>
                    <p className="font-mono text-xs text-[#94A3B8]">{r.category.replace("_", " ")} · due {fmtDate(r.due_date)}{r.interval_days ? ` · every ${r.interval_days}d` : ""}{r.interval_km ? ` · every ${r.interval_km}km` : ""}</p>
                  </div>
                  <StatusBadge status={r.status} />
                  <button onClick={() => delReminder(r.id)} className="text-[#94A3B8] hover:text-[#FF3B30] transition-colors" data-testid={`del-reminder-${r.id}`}><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Service timeline */}
        <div className="card">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Wrench className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Service Timeline</h2></div>
          {services.length === 0 ? <p className="p-5 font-body text-[#94A3B8] text-sm">No services logged yet.</p> : (
            <div className="p-5 space-y-0">
              {services.map((s, i) => (
                <div key={s.id} className="relative pl-6 pb-5 last:pb-0" data-testid={`service-${s.id}`}>
                  <div className="absolute left-0 top-1.5 w-2.5 h-2.5 bg-[#FFB800]" />
                  {i < services.length - 1 && <div className="absolute left-[4px] top-4 bottom-0 w-px bg-white/10" />}
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-head font-700 text-lg uppercase">{s.service_type}</p>
                    <span className="font-mono text-xs text-[#94A3B8]">{fmtDate(s.service_date)}</span>
                  </div>
                  <p className="font-mono text-xs text-[#94A3B8] mt-0.5">${s.cost || 0} · {s.hours != null ? `${s.hours}h · ` : ""}by {s.logged_by_name}</p>
                  {s.parts_replaced && <p className="font-body text-sm text-white/80 mt-1">Parts: {s.parts_replaced}</p>}
                  {s.notes && <p className="font-body text-sm text-[#94A3B8] mt-1">{s.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pre-Start history */}
      <div className="card mt-4" data-testid="prestart-history">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><ClipboardCheck className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Pre-Start Checks</h2></div>
        {prestarts.length === 0 ? <p className="p-5 font-body text-[#94A3B8] text-sm">No pre-start checks logged yet. Operators can log one by scanning the machine QR code.</p> : (
          <div className="divide-y divide-white/10">
            {prestarts.map((p) => (
              <div key={p.id} className="px-5 py-4" data-testid={`prestart-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {p.overall === "fail"
                      ? <span className="inline-flex items-center gap-1 bg-[#FF3B30] text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1"><AlertTriangle className="w-3 h-3" /> Fault</span>
                      : <span className="inline-flex items-center gap-1 bg-[#22C55E] text-black font-mono text-[10px] uppercase tracking-widest px-2 py-1"><ShieldCheck className="w-3 h-3" /> Pass</span>}
                    <p className="font-body font-700 truncate">{p.operator_name}</p>
                  </div>
                  <span className="font-mono text-xs text-[#94A3B8] shrink-0">{fmtDate(p.created_at)}</span>
                </div>
                <p className="font-mono text-xs text-[#94A3B8] mt-1">
                  {p.hours != null ? `${p.hours}h · ` : ""}{p.km != null ? `${p.km}km · ` : ""}{p.items?.length || 0} items checked
                </p>
                {p.overall === "fail" && p.failed_items?.length > 0 && <p className="font-body text-sm text-[#FF3B30] mt-1">Failed: {p.failed_items.join(", ")}</p>}
                {p.notes && <p className="font-body text-sm text-white/80 mt-1">{p.notes}</p>}
                {p.has_photo && <img src={`${API}/prestarts/${p.id}/photo?token=${localStorage.getItem("ironlog_token")}`} alt="Pre-start" className="mt-2 max-h-40 border border-white/10 object-cover" data-testid={`prestart-photo-${p.id}`} />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Defect log (flag history) */}
      <div className="card mt-4" data-testid="flag-history">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><History className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-xl uppercase tracking-wide">Defect Log</h2></div>
        {flagHistory.length === 0 ? <p className="p-5 font-body text-[#94A3B8] text-sm">No defects recorded. This machine has never been flagged.</p> : (
          <div className="divide-y divide-white/10">
            {flagHistory.map((f) => (
              <div key={f.id} className="px-5 py-3.5 flex items-start gap-3" data-testid={`flag-event-${f.id}`}>
                {f.event === "flagged"
                  ? <span className="inline-flex items-center gap-1 bg-[#FF3B30] text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 mt-0.5"><AlertTriangle className="w-3 h-3" /> Flagged</span>
                  : <span className="inline-flex items-center gap-1 bg-[#22C55E] text-black font-mono text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 mt-0.5"><ShieldCheck className="w-3 h-3" /> Resolved</span>}
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm">
                    <span className="font-700">{f.actor}</span>
                    <span className="text-[#94A3B8]"> · {f.source}</span>
                  </p>
                  {f.reason && <p className="font-body text-sm text-[#FF3B30] mt-0.5">{f.reason}</p>}
                </div>
                <span className="font-mono text-[11px] text-[#94A3B8] shrink-0">{fmtDate(f.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-[#1A1C23] border border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-head uppercase text-2xl">Machine QR</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2" data-testid="qr-dialog">
            <div className="bg-white p-4"><QRCodeCanvas value={qrUrl} size={200} /></div>
            <p className="font-mono text-xs text-[#94A3B8] break-all text-center">{qrUrl}</p>
            <button onClick={() => window.print()} className="btn-signal w-full h-12 font-head flex items-center justify-center gap-2"><Printer className="w-4 h-4" /> Print</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
